import React, { useState, useEffect } from 'react';
import Block from '../../../../components/Block';
import Text from '../../../../components/Text';
import Icon from '../../../../components/Icon';
import InteractiveButton from '../Buttons/Interactive';
import { DragAndDropArea } from './styles';
import Heading from './heading/heading';
import Modal from './modal/modal';
import RemoveAllFiles from './trash/allFiles';
import trashImage from '../../../../../assets/illustrations/trash-files.svg';
import ConfirmRemoveSingleFile from './messages/confirmRemoveSingleFile';
import ContentModalTwoButtons from '../ContentModalTwoButtons';
import FilesUploadedList from './files/listFile';
import FileHelper from '../../../../../core/helpers/FileHelper';

interface IProps {
  allowedFormats: string;
  displayFormats: string;
  maxAllowFiles: number;
  maxFileSize: number;
  onFilesClean: () => void;
  onFilesError: (options: any) => void;
  onFilesReady: (files: any) => void;
  title?: string;
  disabled?: boolean;
  clearAll?: boolean;
}

const FileUploader = ({
  allowedFormats,
  displayFormats = '.jpeg',
  maxAllowFiles,
  maxFileSize,
  onFilesClean,
  onFilesError,
  onFilesReady,
  title,
  disabled = false,
  clearAll = false
}: IProps) => {
  const [contentLightBox, setContentLightBox]: any = useState(null);
  const [isLightBoxOpen, setIsLightBoxOpen] = useState(false);
  const [myFiles, setMyFiles] = useState<any>([]);
  const [filesReady, setFilesReady] = useState(false);
  const [numberFormatErrors, setNumberFormatErrors] = useState(0);
  const [disabledDropzone, setDisabledDropzone] = useState(false);
  const [disableFileInput, setDisableFileInput] = useState(false);
  const [totalSize, setTotalSize] = useState(0);
  const [showDragOver, setShowDragOver] = useState(false);
  const handleLightBox = (status: boolean, content: any) => {
    setContentLightBox(content);
    setIsLightBoxOpen(status);
  };

  const removeAll = () => {
    setNumberFormatErrors(0);
    setMyFiles([]);
    setFilesReady(false);
    setTotalSize(0);
  };
  const bytesToMegaBytes = (bytes: any, digits: any, roundTo: any): any =>
    roundTo ? (bytes / (1024 * 1024)).toFixed(digits) : bytes / (1024 * 1024);
  const handleDragEnter = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    if (!disabledDropzone) {
      document.getElementById('drop-area')?.classList.add('isDragged');
    }
  };

  const handleDragOver = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    if (!disabledDropzone) setShowDragOver(true);
  };

  const handleDragLeave = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    if (!disabledDropzone) {
      document.getElementById('drop-area')?.classList.remove('isDragged');
    }
    setShowDragOver(false);
  };

  const handleDrop = (event: any) => {
    event.preventDefault();
    event.stopPropagation();

    if (myFiles.length <= maxAllowFiles) {
      const files = [...event.dataTransfer.files];
      if (files) {
        if (myFiles.length + files.length <= maxAllowFiles) {
          setMyFiles([...myFiles, ...files]);
        }
      }

      document.getElementById('drop-area')?.classList.remove('isDragged');
      setShowDragOver(false);
    }
  };

  const handleRemoveAllFiles = () => {
    handleLightBox(
      true,
      <ContentModalTwoButtons
        img={trashImage}
        title="¿Deseas eliminar todos los archivos?"
        subtitle="Se eliminarán definitivamente todos los archivos que has subido."
        showInfo={false}
        textButton1="Sí, eliminar todo"
        onClickButton1={() => {
          handleLightBox(false, null);
          removeAll();
          onFilesClean();
          setDisableFileInput(false);
          setDisabledDropzone(false);
        }}
        textButton2="Cancelar"
        onClickButton2={() => {
          handleLightBox(false, null);
        }}
      />
    );
  };

  const isAllowedFormat = (f: any) => {
    const ext = `.${FileHelper.getFileExtension(f.name)}`;
    const formats = allowedFormats.split(',');
    return formats.includes(ext.toLowerCase());
  };

  const handleRemoveFormatError = () => {
    if (numberFormatErrors > 0) {
      setNumberFormatErrors(numberFormatErrors - 1);
    }
  };
  const handleAddFormatError = () => {
    setNumberFormatErrors(numberFormatErrors + 1);
  };

  const handleRemoveSingleFile = (df: any) => {
    handleLightBox(
      true,
      <ConfirmRemoveSingleFile
        handleRemoveSingleFile={() => {
          handleLightBox(false, null);
          const CopyFiles = myFiles;
          const listRestFiles: any = [];

          CopyFiles.forEach((file: any) => {
            if (file !== df) {
              listRestFiles.push(file);
            }
          });
          setMyFiles(listRestFiles);
          setTotalSize(totalSize - df.size);
          setDisableFileInput(false);
          setDisabledDropzone(false);
          if (totalSize === 0) {
            onFilesClean();
          }
          if (!isAllowedFormat(df)) {
            handleRemoveFormatError();
          }
        }}
        handleCloseModal={() => {
          handleLightBox(false, null);
        }}
      />
    );
  };
  const currentSizeTotal = 0;
  const handleFileSelected = (e: any) => {
    const files = Array.from(e.target.files);
    if (files) {
      if (myFiles.length + files.length <= maxAllowFiles) {
        setMyFiles([...myFiles, ...files]);
      }
    }
  };

  useEffect(() => {
    if (clearAll) {
      removeAll();
      onFilesClean();
      setDisableFileInput(false);
      setDisabledDropzone(false);
      return;
    }
    let acumulateSize = 0;
    myFiles.forEach((myFile: any) => {
      if (!isAllowedFormat(myFile)) {
        setNumberFormatErrors(numberFormatErrors + 1);
        onFilesError({ filesCount: myFiles.length });
      }
      acumulateSize += myFile.size;
    });
    if (myFiles.length >= maxAllowFiles) {
      setDisabledDropzone(true);
      setDisableFileInput(true);
      onFilesError({ filesCount: myFiles.length });
    }
    setTotalSize(acumulateSize);
  }, [myFiles, clearAll]);

  useEffect(() => {
    const r1 = bytesToMegaBytes(totalSize, 2, true) <= maxFileSize;
    const r2 = bytesToMegaBytes(totalSize, 2, true) > 0;
    const r3 = numberFormatErrors === 0;
    const r4 = myFiles.length <= maxAllowFiles;
    if (r1 && r2 && r3 && r4) {
      setFilesReady(true);
      if (filesReady && myFiles.length) {
        onFilesReady(myFiles);
      }
    } else {
      onFilesError({ filesCount: myFiles.length });
    }
  }, [numberFormatErrors, totalSize, filesReady]);

  return (
    <Block
      data-testid="cmp-fileUploader"
      customized
      margin="4px 0px 0px 0px"
      className="other"
    >
      <Heading
        title={title ?? ''}
        subtitle={`Sólo puedes subir ${maxAllowFiles} ${
          maxAllowFiles > 1 ? 'archivos' : 'archivo'
        } en formato ${displayFormats}. <b>(Carga máxima: ${maxFileSize} MB)</b>`}
      />

      {totalSize > 0 && (
        <Block
          customized
          width="100%"
          borderRadius="4px"
          overflow="hidden"
          margin="16px 0px 0px"
        >
          <Block
            customized
            width="100%"
            display="flex"
            justifyContent="flex-start"
            flexDirection="row"
            minHeight="32px"
            alignItems="center"
            backgroundColor="#EFF1F6"
            padding="8px"
          >
            <Block
              customized
              flexDirection="column"
              maxWidth="auto"
              alignItems="flex-start"
            >
              <div
                style={{
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: '16px',
                  color: '#4F6168'
                }}
              >
                Tamaño Total:{' '}
                <Text>
                  <Text
                    fontWeight="700"
                    color={
                      bytesToMegaBytes(totalSize, 2, true) > maxFileSize
                        ? '#E8212B'
                        : '#161D1F'
                    }
                  >
                    {totalSize > 0 ? bytesToMegaBytes(totalSize, 2, true) : 0}{' '}
                    MB
                  </Text>{' '}
                  /{' '}
                  <Text fontWeight="700" color="#161D1F">
                    {' '}
                    {maxFileSize} MB
                  </Text>
                </Text>
              </div>
            </Block>
          </Block>
          {bytesToMegaBytes(totalSize, 2, true) > maxFileSize && (
            <Block
              customized
              padding="8px"
              minHeight="32px"
              backgroundColor="#FDDADB"
              flexDirection="row"
            >
              <Block
                flexDirection="column"
                customized
                maxWidth="16px"
                margin="0px 5px 0px 0px"
              >
                <Icon name="alert-triangle" size="16px" color="#BA1821" />
              </Block>
              <Block customized flexDirection="column">
                {myFiles.length === 1 ? (
                  <Text color="#BA1821" fontSize="12px">
                    ¡Ups! Tu archivo pesa demasiado. Comprímelo y vuélvelo a
                    subir.
                  </Text>
                ) : (
                  <Text color="#BA1821" fontSize="12px">
                    ¡Ups! Te has pasado de la carga máxima. Elimina el archivo
                    más pesado.
                  </Text>
                )}
              </Block>
            </Block>
          )}
        </Block>
      )}
      <DragAndDropArea
        id="drop-area"
        data-testid="drag-and-drop-area"
        onDragOver={(event: any) => {
          handleDragOver(event);
        }}
        onDragEnter={(event: any) => {
          handleDragEnter(event);
        }}
        onDrop={(event: any) => {
          handleDrop(event);
        }}
        onDragLeave={(event: any) => {
          handleDragLeave(event);
        }}
        className={`${!disabledDropzone ? '' : 'isDisabled'} ${
          disabled ? 'isDisabled' : ''
        }`}
      >
        <input
          accept={allowedFormats}
          onChange={(event: any) => handleFileSelected(event)}
          data-testid="input-file-id"
          value=""
          id="input"
          type="file"
          multiple={maxAllowFiles > 1}
          style={{ display: 'none' }}
        />
        {showDragOver && (
          <span className="messageOver">Arrastra tu archivo aquí</span>
        )}
        {!showDragOver && (
          <InteractiveButton
            disabled={disableFileInput || disabled}
            cursor={disableFileInput || disabled ? 'default' : 'pointer'}
            borderRadius="4px"
            bkgColorEnabled="none"
            bkgColorHover={!disableFileInput || !disabled ? '#E2DFFD' : 'none'}
            bkgColorFocus={!disableFileInput || !disabled ? '#C6C0FC' : 'none'}
            bkgColorPressed={
              !disableFileInput || !disabled ? '#C6C0FC' : 'none'
            }
            customized
            alignContent="center"
            alignItems="center"
            justifyContent="center"
            width="141px"
            height="40px"
            onClick={() => {
              if (myFiles.length <= maxAllowFiles) {
                document.getElementById('input')?.click();
              }
            }}
            as="button"
          >
            <Block
              customized
              alignContent="center"
              alignItems="center"
              flexDirection="row"
              justifyContent="center"
            >
              <Icon name="upload" size={19} color="#5B36F2" />
              <Text
                fontSize="12px"
                lineHeight="16px"
                fontWeight="700"
                textAlign="center"
                color="#5B36F2"
              >
                {'Subir archivo(s)'}
              </Text>
            </Block>
          </InteractiveButton>
        )}
      </DragAndDropArea>

      {myFiles.length > 0 && (
        <>
          <Block
            customized
            justifyContent="center"
            alignItems="center"
            margin="24px 0px 8px"
            flexDirection="row"
            minHeight="20px"
          >
            <Block
              customized
              flexDirection="column"
              justifyContent="center"
              alignItems="flex-start"
            >
              <Text color="#161D1F" fontSize="12px" lineHeight="16px">
                {myFiles.length} archivo(s)
              </Text>
            </Block>
            <Block
              customized
              flexDirection="column"
              justifyContent="center"
              alignItems="flex-end"
            >
              {maxAllowFiles > 0 && (
                <RemoveAllFiles handleRemoveAllFiles={handleRemoveAllFiles} />
              )}{' '}
            </Block>
          </Block>
          <FilesUploadedList
            acceptedFiles={myFiles}
            handleRemoveSingleFile={handleRemoveSingleFile}
            maxAllowFiles={maxAllowFiles}
            maxFileSize={maxFileSize}
            filesStored={myFiles.length}
            currentSizeTotal={currentSizeTotal}
            handleAddFormatError={handleAddFormatError}
            handleRemoveFormatError={handleRemoveFormatError}
            totalSize={totalSize}
            allowedFormats={allowedFormats}
          />
        </>
      )}
      <Modal
        contentLightBox={contentLightBox}
        isLightBoxOpen={isLightBoxOpen}
        onClick={() => handleLightBox(false, null)}
      />
    </Block>
  );
};

export default FileUploader;
